import { useRef, useCallback, useEffect } from 'react';

import { Loading } from '../loading/Loading';

import { InfiniteScrollLoader } from './InfiniteScrollLoader';

interface InfiniteScrollProps {
  load: () => void;
  hasMore: boolean;
  isLoading?: boolean;
  loader?: React.ReactNode;
  initialLoader?: React.ReactNode;
  children?: React.ReactNode;
  endMessage?: React.ReactNode;
  threshold?: number;
}

export const InfiniteScroll = ({
  load,
  hasMore,
  loader = <InfiniteScrollLoader />,
  initialLoader = <Loading />,
  isLoading = false,
  children,
  endMessage,
  threshold = 0,
}: InfiniteScrollProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      // Check if the sentinel element is intersecting, and if so, call the load function
      if (entries[0].isIntersecting && hasMore) {
        load();
      }
    },
    [hasMore, load],
  );

  useEffect(() => {
    // Create a new IntersectionObserver when the component mounts
    observerRef.current = new IntersectionObserver(handleIntersect, {
      root: null,
      rootMargin: '0px',
      threshold,
    });

    // Attach the observer to the sentinel element
    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    // Clean up the observer when the component unmounts
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersect, load, threshold]);

  useEffect(() => {
    // When the hasMore prop changes, disconnect the previous observer and reattach it to the new sentinel element
    if (observerRef.current && sentinelRef.current) {
      observerRef.current.disconnect();
      observerRef.current.observe(sentinelRef.current);
    }
  }, [hasMore]);

  return (
    <>
      {isLoading && initialLoader}
      {children}
      <div ref={sentinelRef} style={{ width: '100%' }}>
        {hasMore && loader}
      </div>
      {!hasMore && endMessage}
    </>
  );
};
